<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card shadow-sm border-0">
          <div class="card-body p-4">
            <h4 class="card-title text-primary mb-4">Create New Document</h4>

            <form @submit.prevent="submitDocument">
              <div class="row">
                <div class="col-md-6 mb-4">
                  <div class="form-group h-100 d-flex flex-column">
                    <label class="fw-bold mb-1">Upload Document <span class="text-muted fw-normal fst-italic ms-1">(PDF
                        only, Max 5MB)</span> <span class="text-danger">*</span></label>

                    <div
                      class="dropzone-area flex-grow-1 d-flex flex-column align-items-center justify-content-center p-4 rounded"
                      style="border: 2px dashed #dee2e6; transition: all 0.3s ease;" :class="{
                        'border-primary bg-light': isDragging,
                        'opacity-50': loading,
                        'cursor-not-allowed': loading
                      }" @dragover.prevent="!loading && (isDragging = true)" @dragleave.prevent="isDragging = false"
                      @drop.prevent="!loading && handleDrop($event)" @click="!loading && triggerFileInput()">
                      <input type="file" ref="fileInput" @change="handleFileUpload" accept="application/pdf"
                        class="d-none" :disabled="loading" />

                      <div v-if="!file" class="text-center text-muted" style="pointer-events: none;">
                        <i class="mdi mdi-cloud-upload-outline display-3"></i>
                        <h5 class="mt-2 text-dark">Click or drag file here</h5>
                        <p class="mb-0 small">Maximum file size 5MB.</p>
                      </div>

                      <div v-else class="text-center w-100">

                        <div
                          class="document-preview-wrapper mx-auto mb-3 bg-white border shadow-sm rounded overflow-hidden d-flex flex-column"
                          style="height: 280px;">

                          <iframe v-if="pdfPreviewUrl" :src="pdfPreviewUrl + '#toolbar=0&navpanes=0&scrollbar=0&view=FitH'"
                            class="w-100 flex-grow-1 border-0" title="PDF Preview" scrolling="no" style="pointer-events: none; overflow: hidden;"></iframe>

                          <div v-else
                            class="flex-grow-1 d-flex flex-column align-items-center justify-content-center p-3">
                            <i class="mdi mdi-file-pdf-box text-danger display-4"></i>
                            <span class="text-muted">Preview not available</span>
                          </div>

                          <div class="bg-light p-2 d-flex justify-content-between align-items-center border-top">
                            <div class="text-truncate fw-bold text-dark text-start" style="max-width: 70%;">{{ file.name
                              }}</div>
                            <div class="text-muted small">{{ formatBytes(file.size) }}</div>
                          </div>
                        </div>

                        <b-button size="sm" variant="outline-danger" @click.stop="removeFile" :disabled="loading">
                          <i class="mdi mdi-trash-can-outline me-1"></i> Remove File
                        </b-button>
                      </div>
                    </div>

                    <small v-if="fileError" class="text-danger mt-2 d-block fw-bold">
                      <i class="mdi mdi-alert-circle-outline"></i> {{ fileError }}
                    </small>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group mb-4 mt-md-0 mt-3">
                    <label class="fw-bold mb-1" for="documentTitleInput">Title <span
                        class="text-danger">*</span></label>
                    <input id="documentTitleInput" v-model="documentTitle" type="text"
                      class="form-control form-control-lg" placeholder="e.g AGM Meeting Minutes 2024" required
                      :disabled="loading" />
                  </div>

                  <div class="form-group mb-4">
                    <label class="fw-bold mb-1" for="documentTypeSelect">Document Type <span
                        class="text-danger">*</span></label>
                    <b-form-select id="documentTypeSelect" v-model="selectedType" class="form-select form-select-lg"
                      required :disabled="loading">
                      <option value="" disabled>Select Document Type</option>
                      <option v-for="type in documenttypes" :key="type.id" :value="type.id">
                        {{ type.name }}
                      </option>
                    </b-form-select>
                  </div>
                </div>
              </div>

              <div class="d-flex justify-content-end mt-4 border-top pt-4">
                <router-link to="" custom v-slot="{ navigate }">
                  <button @click="$router.back()" class="btn btn-light btn-lg me-3 px-4 shadow-sm" :disabled="loading">
                    Cancel
                  </button>
                </router-link> <b-button type="submit" variant="primary" size="lg" class="px-5 shadow-sm"
                  :disabled="loading || !file || fileError !== ''">
                  <b-spinner small v-if="loading" class="me-2"></b-spinner>
                  <i v-else class="mdi mdi-content-save me-2"></i> Add Document
                </b-button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import axios from "axios";
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { documentTypeMethods, documentTypeComputed } from "@/state/helpers";

export default {
  page: {
    title: "New Document",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Layout, PageHeader
  },
  data() {
    return {
      title: "New Document",
      items: [
        { text: "Dashboard", to: "/" },
        { text: "Documents", to: "/documents" },
        { text: "New Document", active: true }
      ],
      documentTitle: "",
      selectedType: "",
      file: null,
      pdfPreviewUrl: null,
      fileError: "",
      loading: false,
      isDragging: false,
    };
  },
  computed: {
    ...documentTypeComputed,
  },
  mounted() {
    this.fetchDocumentTypes();
  },
  methods: {
    ...documentTypeMethods,

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleDrop(event) {
      this.isDragging = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },

    processFile(file) {
      this.fileError = "";

      if (!file) {
        this.file = null;
        return;
      }

      // Check file type
      if (file.type !== "application/pdf") {
        this.fileError = "Only PDF files are allowed.";
        this.file = null;
        return;
      }

      // Check file size (5MB = 5 * 1024 * 1024 bytes)
      if (file.size > 5 * 1024 * 1024) {
        this.fileError = "File size exceeds 5MB limit.";
        this.file = null;
        return;
      }

      this.file = file;

      // Autofill title if empty
      if (!this.documentTitle) {
        // Remove .pdf extension
        let name = file.name.replace(/\.pdf$/i, "");
        this.documentTitle = name;
      }
    },

    formatBytes(bytes, decimals = 2) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    },

    handleFileUpload(event) {
      const selectedFile = event.target.files[0] || event.dataTransfer.files[0];

      if (selectedFile && selectedFile.type === 'application/pdf') {
        this.file = selectedFile;

        // Create a temporary local URL for the iframe to read
        this.pdfPreviewUrl = URL.createObjectURL(selectedFile);

        // Autofill title if empty
        if (!this.documentTitle || this.documentTitle.trim() === "") {
          let name = selectedFile.name.replace(/\.pdf$/i, "");
          this.documentTitle = name;
        }

        this.fileError = '';
      } else {
        this.fileError = 'Please upload a valid PDF file.';
      }
    },

    removeFile() {
      // Release the memory before clearing the variables
      if (this.pdfPreviewUrl) {
        URL.revokeObjectURL(this.pdfPreviewUrl);
      }

      this.file = null;
      this.pdfPreviewUrl = null;
      this.$refs.fileInput.value = ''; // Reset the input element
    },

    async submitDocument() {
      if (!this.file || this.fileError) {
        this.$toast.error("Please upload a valid PDF document.");
        return;
      }
      if (!this.documentTitle || !this.selectedType) {
        this.$toast.error("Please fill in all required fields.");
        return;
      }

      this.loading = true;

      let formData = new FormData();
      formData.append("title", this.documentTitle);
      formData.append("document_type_id", this.selectedType);
      formData.append("file", this.file);

      try {
        const response = await axios.post("/create-document", formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });

        if (response.status === 200 || response.status === 201) {
          this.$toast.success("Document added successfully");
          this.$router.push("/documents");
        } else {
          this.$toast.error(response?.data?.message || "An error occurred");
        }
      } catch (error) {
        this.$toast.error(error?.response?.data?.message || "Failed to add document");
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.dropzone-area {
  border: 2px dashed #ced4da;
  background-color: #f8f9fa;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 250px;
}

.dropzone-area:hover {
  border-color: #556ee6;
  background-color: #f1f3f5;
}

.document-thumbnail {
  width: 140px;
  height: 160px;
  border-radius: 8px;
}
</style>